import { createTheme } from '@mui/material/styles'
declare module '@mui/material/styles' {
  interface PaletteColor {
    lighter?: string
    background?: string
  }

  interface SimplePaletteColorOptions {
    lighter?: string
    background?: string
  }
  interface TypographyVariants {
    smallText: React.CSSProperties
    captionSmall: React.CSSProperties
    modal: React.CSSProperties
    bodySmall: React.CSSProperties
    text: React.CSSProperties
    smallTextNormal: React.CSSProperties
    textBold : React.CSSProperties
  }
  interface TypographyVariantsOptions {
    smallText?: React.CSSProperties
    captionSmall?: React.CSSProperties
    modal?: React.CSSProperties
    bodySmall?: React.CSSProperties
    text?: React.CSSProperties
    smallTextNormal?: React.CSSProperties
    textBold?: React.CSSProperties
    bodyLarge?: React.CSSProperties
  }
}
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    smallText: true
    captionSmall: true
    modal: true
    bodySmall: true
    smallTextNormal: true
    textBold: true
    bodyLarge: true
  }
}
const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 768,
      lg: 1024,
      xl: 1200,
    },
  },
  palette: {
    primary: {
      main: '#444D4E',
      light: '#5F686A',
      lighter: '#DADCDD',
      background: '#F1F1F2',
    },
    secondary: {
      main: '#F9A51A',
    },
    error: {
      main: '#991314',
    },
  },
  typography: {
    fontFamily: ['DIN', 'sans-serif'].join(','),
    allVariants: {
      color: '#444D4E',
    },
    modal: {
      fontFamily: ['DIN', 'sans-serif'].join(','),
      fontSize: '20px',
      lineHeight: '28px',
      fontWeight: '500',
      color: '#444D4E',
      padding: '40px',
    },
    h1: {
      fontSize: '40px',
      lineHeight: '48px',
      fontWeight: '700',
    },
    h2: {
      fontSize: '32px',
      lineHeight: '40px',
      fontWeight: '700',
    },
    h3: {
      fontSize: '24px',
      lineHeight: '32px',
      fontWeight: '500',
    },
    h4: {
      fontSize: '20px',
      lineHeight: '28px',
      fontWeight: '500',
    },
    h5: {
      fontWeight: '500',
      fontSize: '16px',
      lineHeight: '20px',
      letterSpacing: '1px',
    },
    h6: {
      fontFamily: ['DIN', 'sans-serif'].join(','),
      fontWeight: '500',
      fontSize: '14px',
      lineHeight: '18px',
    },
    smallText: {
      fontFamily: ['DIN', 'sans-serif'].join(','),
      fontSize: '14px',
      fontWeight: '700',
      color: '#444D4E',
    },
    smallTextNormal: {
      fontFamily: ['DIN', 'sans-serif'].join(','),
      fontSize: '14px',
      fontWeight: '400',
      color: '#444D4E',
      lineHeight: '20px',
    },
    body1: {
      fontFamily: ['DIN', 'sans-serif'].join(','),
      fontSize: '16px',
      lineHeight: '24px',
      marginBottom: '0 !important',
    },
    bodyLarge: {
      fontFamily: ['DIN', 'sans-serif'].join(','),
      fontSize: '20px',
      lineHeight: '24px',
      fontWeight: '400',
      letterSpacing: '1px',
    },
    captionSmall: {
      fontFamily: ['DIN', 'sans-serif'].join(','),
      fontSize: '12px',
      lineHeight: '16px',
      fontWeight: '500',
      color: '#444D4E',
    },
    bodySmall: {
      fontFamily: ['DIN', 'sans-serif'].join(','),
      fontWeight: '500',
      fontSize: '14px',
      lineHeight: '20px',
      color: '#444D4E',
    },
    text: {
      fontFamily: ['DIN', 'sans-serif'].join(','),
      fontSize: '16px',
      lineHeight: '24px',
    },
    textBold: {
      fontFamily: ['DIN', 'sans-serif'].join(','),
      fontSize: '16px',
      lineHeight: '24px',
      fontWeight: '700',
    },
  },
  components: {
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          marginTop: '8px',
          fontSize: '16px',
          border: '1px solid #DADCDD',
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.25) !important',
          borderRadius: '4px',
          '@media (max-width: 480px)': {
            fontSize: '14px',
            marginTop: '4px',
            maxWidth: '90vw',
            overflow: 'hidden',
          },
          '@media (max-width: 800px)': {
            fontSize: '15px',
            marginTop: '6px',
            maxWidth: '95vw',
          },
        },
        list: {
          padding: '16px',
          '@media (max-width: 480px)': {
            padding: '12px',
          },
          '@media (max-width: 800px)': {
            padding: '14px',
          },
          '& li': {
            fontFamily: 'Overpass',
            backgroundColor: 'white!important',
            padding: '0',
            '@media (max-width: 480px)': {
              fontSize: '14px',
              minHeight: '36px',
              display: 'flex',
              alignItems: 'center',
            },
            '@media (max-width: 800px)': {
              fontSize: '15px',
              minHeight: '40px',
              display: 'flex',
              alignItems: 'center',
            },
          },
          '& li:hover': {
            color: '#292929',
            fontWeight: '700',
          },
          '& li + li': {
            marginTop: '8px',
            '@media (max-width: 480px)': {
              marginTop: '6px',
            },
            '@media (max-width: 800px)': {
              marginTop: '7px',
            },
          },
        },
      },
    },
    MuiModal: {
      styleOverrides: {
        root: {
          // Ensure modal doesn't overflow due to scrollbar
          '@media (max-width: 480px)': {
            padding: '20px',
          },
          '@media (max-width: 800px)': {
            padding: '30px',
          },
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          // Ensure backdrop covers full viewport
          '@media (max-width: 480px)': {
            paddingRight: '0 !important',
          },
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          '@media (max-width: 768px)': {
            fontSize: '14px',
          },
          '@media (max-width: 480px)': {
            fontSize: '12px',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          '@media (max-width: 768px)': {
            padding: '8px 4px',
            fontSize: '14px',
          },
          '@media (max-width: 480px)': {
            padding: '6px 2px',
            fontSize: '12px',
            '& .MuiTypography-root': {
              fontSize: '12px !important',
            },
          },
        },
        head: {
          '@media (max-width: 768px)': {
            fontSize: '12px',
            padding: '8px 4px',
          },
          '@media (max-width: 480px)': {
            fontSize: '10px',
            padding: '6px 2px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          '@media (max-width: 768px)': {
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#666666',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: '#555555',
              },
              '&:active': {
                backgroundColor: '#444444',
              },
            },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          '@media (max-width: 768px)': {
            fontSize: '14px',
            padding: '8px 16px',
          },
          '@media (max-width: 480px)': {
            fontSize: '12px',
            padding: '6px 12px',
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          '@media (max-width: 768px)': {
            '&.MuiTypography-h1': {
              fontSize: '32px !important',
              lineHeight: '40px !important',
            },
            '&.MuiTypography-h2': {
              fontSize: '28px !important',
              lineHeight: '36px !important',
            },
            '&.MuiTypography-h3': {
              fontSize: '20px !important',
              lineHeight: '28px !important',
            },
            '&.MuiTypography-h4': {
              fontSize: '18px !important',
              lineHeight: '24px !important',
            },
            '&.MuiTypography-h5': {
              fontSize: '14px !important',
              lineHeight: '18px !important',
            },
            '&.MuiTypography-h6': {
              fontSize: '12px !important',
              lineHeight: '16px !important',
            },
            '&.MuiTypography-body1': {
              fontSize: '14px !important',
              lineHeight: '22px !important',
            },
            // Custom variant responsive styles
            '&[class*="smallText"]': {
              fontSize: '12px !important',
            },
            '&[class*="smallTextNormal"]': {
              fontSize: '12px !important',
              lineHeight: '18px !important',
            },
            '&[class*="bodyLarge"]': {
              fontSize: '18px !important',
              lineHeight: '22px !important',
            },
            '&[class*="captionSmall"]': {
              fontSize: '11px !important',
              lineHeight: '14px !important',
            },
            '&[class*="bodySmall"]': {
              fontSize: '12px !important',
              lineHeight: '18px !important',
            },
            '&[class*="text"]': {
              fontSize: '14px !important',
              lineHeight: '22px !important',
            },
            '&[class*="textBold"]': {
              fontSize: '14px !important',
              lineHeight: '22px !important',
            },
          },
          '@media (max-width: 480px)': {
            '&.MuiTypography-h1': {
              fontSize: '28px !important',
              lineHeight: '36px !important',
            },
            '&.MuiTypography-h2': {
              fontSize: '24px !important',
              lineHeight: '32px !important',
            },
            '&.MuiTypography-h3': {
              fontSize: '18px !important',
              lineHeight: '24px !important',
            },
            '&.MuiTypography-h4': {
              fontSize: '16px !important',
              lineHeight: '22px !important',
            },
            '&.MuiTypography-h5': {
              fontSize: '13px !important',
              lineHeight: '16px !important',
            },
            '&.MuiTypography-h6': {
              fontSize: '11px !important',
              lineHeight: '14px !important',
            },
            '&.MuiTypography-body1': {
              fontSize: '13px !important',
              lineHeight: '20px !important',
            },
            // Custom variant responsive styles
            '&[class*="smallText"]': {
              fontSize: '11px !important',
            },
            '&[class*="smallTextNormal"]': {
              fontSize: '11px !important',
              lineHeight: '16px !important',
            },
            '&[class*="bodyLarge"]': {
              fontSize: '16px !important',
              lineHeight: '20px !important',
            },
            '&[class*="captionSmall"]': {
              fontSize: '10px !important',
              lineHeight: '13px !important',
            },
            '&[class*="bodySmall"]': {
              fontSize: '11px !important',
              lineHeight: '16px !important',
            },
            '&[class*="text"]': {
              fontSize: '13px !important',
              lineHeight: '20px !important',
            },
            '&[class*="textBold"]': {
              fontSize: '13px !important',
              lineHeight: '20px !important',
            },
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          '@media (max-width: 800px)': {
            minHeight: '44px',
            '& .MuiTabs-scroller': {
              overflow: 'auto !important',
            },
            '& .MuiTabs-scrollButtons': {
              '&.Mui-disabled': {
                opacity: 0.3,
              },
            },
          },
        },
        indicator: {
          '@media (max-width: 800px)': {
            height: '3px',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          '@media (max-width: 800px)': {
            fontSize: '0.875rem',
            minWidth: '80px',
            padding: '8px 12px',
            minHeight: '44px',
            textTransform: 'none',
            '&:not(:last-child)': {
              marginRight: '4px',
            },
          },
          '@media (max-width: 480px)': {
            fontSize: '0.75rem',
            minWidth: '60px',
            padding: '6px 8px',
            minHeight: '40px',
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: () => ({
          width: 52,
          height: 32,
          padding: 0,
          margin: '0 0 0 12px !important',
          '& .MuiSwitch-switchBase': {
            padding: 0,
            margin: 2,
            top: '2px',
            transitionDuration: '300ms',
            '&.Mui-checked': {
              transform: 'translateX(22px)',
              color: '#fff',
              '& + .MuiSwitch-track': {
                backgroundColor: '#F9A51A',
                opacity: 1,
                border: 'none',
              },
              '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.5,
              },
              '& .MuiSwitch-thumb': {
                boxSizing: 'border-box',
                backgroundColor: '#fff',
              },
              '& .MuiSwitch-thumb:before': {
                content: '""',
                height: '15px',
                width: '15px',
                display: 'block',
                backgroundImage: `url('data:image/svg+xml;utf8,<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill="${encodeURIComponent(
                  '#444D4E',
                )}" d="M5.99998 10.7799L3.21998 7.9999L2.27332 8.9399L5.99998 12.6666L14 4.66656L13.06 3.72656L5.99998 10.7799Z"/></svg>')`,
              },
            },
            '&.Mui-focusVisible .MuiSwitch-thumb': {
              color: '#33cf4d',
              border: '6px solid #fff',
            },
          },
          '& .MuiSwitch-thumb': {
            boxSizing: 'border-box',
            width: 24,
            height: 24,
            boxShadow: 'none',
            backgroundColor: '#5F686A',
            border: '4px solid #fff;',
          },
          '& .MuiSwitch-track': {
            boxSizing: 'border-box',
            borderRadius: 32 / 2,
            opacity: 1,
            backgroundColor: '#fff',
            border: '2px solid #DADCDD',
            transition: theme.transitions.create(['background-color, borderColor'], {
              duration: 500,
            }),
          },
        }),
      },
    },
  },
})
export default theme
